import { useCallback, useEffect, useRef, type KeyboardEvent as ReactKeyboardEvent } from "react";
import type { VisibleCpvNode } from "./cpvHierarchy";

const ITEM_SELECTOR = "[role='treeitem']";

interface Options {
  rows: VisibleCpvNode[];
  expandedIds: ReadonlySet<string>;
  onToggleSelect: (code: string) => void;
  onToggleExpand: (code: string) => void;
  onEscape?: () => void;
}

/**
 * Keyboard behaviour for a `role="tree"`, per the WAI-ARIA Authoring Practices: the whole tree is a
 * single tab stop, the arrow keys move between rows, and Escape leaves it.
 *
 * Rows are resolved from the DOM on each key press rather than registered by the caller, because
 * expanding a node changes the set on the next render — the same reason `useRovingToolbar` does.
 */
export const useCpvTreeKeyboard = ({
  rows,
  expandedIds,
  onToggleSelect,
  onToggleExpand,
  onEscape,
}: Options) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const activeIndex = useRef(0);

  const latest = useRef({ rows, expandedIds, onToggleSelect, onToggleExpand, onEscape });
  useEffect(() => {
    latest.current = { rows, expandedIds, onToggleSelect, onToggleExpand, onEscape };
  });

  const items = useCallback(
    () => Array.from(containerRef.current?.querySelectorAll<HTMLElement>(ITEM_SELECTOR) ?? []),
    [],
  );

  const syncTabIndexes = useCallback(() => {
    const rendered = items();
    if (rendered.length === 0) return;
    activeIndex.current = Math.min(activeIndex.current, rendered.length - 1);
    rendered.forEach((row, index) => {
      row.tabIndex = index === activeIndex.current ? 0 : -1;
    });
  }, [items]);

  // No dependency array: expanding or collapsing changes the row set on any render, and the index
  // is clamped when it shrinks.
  useEffect(syncTabIndexes);

  const focusAt = useCallback(
    (index: number) => {
      const rendered = items();
      if (rendered.length === 0) return;
      const next = Math.max(0, Math.min(index, rendered.length - 1));
      activeIndex.current = next;
      syncTabIndexes();
      rendered[next].focus();
      // Not implemented in jsdom, so the tests would throw on every arrow key.
      rendered[next].scrollIntoView?.({ block: "nearest" });
    },
    [items, syncTabIndexes],
  );

  const onKeyDown = useCallback(
    (event: ReactKeyboardEvent) => {
      if (!(event.target as HTMLElement).closest(ITEM_SELECTOR)) return;
      if (event.metaKey || event.ctrlKey || event.altKey) return;

      const current = activeIndex.current;
      const state = latest.current;
      const row = state.rows[current];
      if (!row) return;
      const { node } = row;
      const hasChildren = node.children.length > 0;
      const isExpanded = state.expandedIds.has(node.code);

      switch (event.key) {
        case "ArrowDown":
          focusAt(current + 1);
          break;
        case "ArrowUp":
          focusAt(current - 1);
          break;
        case "ArrowRight":
          if (!hasChildren) return;
          if (isExpanded) focusAt(current + 1);
          else state.onToggleExpand(node.code);
          break;
        case "ArrowLeft":
          if (hasChildren && isExpanded) state.onToggleExpand(node.code);
          else if (row.parentIndex != null) focusAt(row.parentIndex);
          else return;
          break;
        case "Home":
          focusAt(0);
          break;
        case "End":
          focusAt(state.rows.length - 1);
          break;
        case " ":
        case "Enter":
          state.onToggleSelect(node.code);
          break;
        case "Escape":
          // Nowhere to send focus means swallowing Escape would trap the user inside whatever
          // encloses the tree.
          if (!state.onEscape) return;
          state.onEscape();
          break;
        default:
          return;
      }

      event.preventDefault();
      // Escape in particular must not reach an enclosing dialog — leaving the tree is not a request
      // to close what contains it.
      event.stopPropagation();
    },
    [focusAt],
  );

  const onFocus = useCallback(
    (event: { target: EventTarget | null }) => {
      const item = (event.target as HTMLElement | null)?.closest<HTMLElement>(ITEM_SELECTOR);
      if (!item) return;
      const index = items().indexOf(item);
      if (index === -1) return;
      activeIndex.current = index;
      syncTabIndexes();
    },
    [items, syncTabIndexes],
  );

  return { containerRef, treeHandlers: { onKeyDown, onFocus } };
};
